import { Dropdown as HakiDropdown, Input as HakiInput } from "@hakistudio/hakiui";
import { ChevronDown } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/cn";

/**
 * Layout-safe wrappers around HakiUI's form controls.
 *
 * Both `Input` and `Dropdown` render an outer `w-full` container, and the
 * `className` they accept lands on an *inner* element — so layout utilities
 * passed to them never reach the box that actually sizes the control. Dropped
 * into a flex row that makes them expand to the full width and crush their
 * siblings; on the Teams screen it collapsed the member's name to zero width.
 *
 * These wrappers own the outer box, so `className` means what it looks like it
 * means: pass `flex-1`, `w-32`, `shrink-0` and get that behaviour.
 */

export const Input = forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof HakiInput> & { className?: string }
>(function Input({ className, ...props }, ref) {
  return (
    <div className={cn("min-w-0", className)}>
      <HakiInput ref={ref} {...props} />
    </div>
  );
});

export function Select({
  className,
  ...props
}: React.ComponentProps<typeof HakiDropdown> & { className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <HakiDropdown {...props} />
    </div>
  );
}

const SELECT_SIZE = {
  sm: "min-h-8 py-1 pl-2.5 pr-8 text-xs",
  md: "min-h-9 py-2 pl-3 pr-9 text-sm",
  lg: "min-h-10 py-2.5 pl-3.5 pr-9 text-base",
} as const;

/**
 * The platform `<select>`, styled to sit next to `Input`.
 *
 * HakiUI's `Dropdown` renders its menu in a `document.body` portal, which the
 * browser paints below a `position: fixed` modal in some stacking situations —
 * so inside a dialog the menu opened invisibly. A native control has no such
 * problem, and on a phone it brings up the system picker, which is the better
 * way to choose from a short list anyway.
 */
export const NativeSelect = forwardRef<
  HTMLSelectElement,
  Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size" | "children"> & {
    options: { label: string; value: string; disabled?: boolean }[];
    size?: "sm" | "md" | "lg";
  }
>(function NativeSelect({ className, options, size = "sm", ...props }, ref) {
  return (
    <div className={cn("relative min-w-0", className)}>
      <select
        ref={ref}
        className={cn(
          "w-full appearance-none rounded-md border border-line bg-raised text-ink transition-colors",
          "focus-visible:border-accent focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-60",
          SELECT_SIZE[size],
        )}
        {...props}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
    </div>
  );
});
